import { FIELD_NAMES, PERSONAL_INFO_FIELD_NAMES } from '../../constants';
import { getInitialFormValues } from '../contact-information/formValues';
import { recordCustomProfileEvent } from '../analytics';

// These fields save through the form app first and only then write to the
// profile, so the edit view needs to know to take that path.
const FORM_ONLY_UPDATE_FIELDS = [
  FIELD_NAMES.EMAIL,
  FIELD_NAMES.HOME_PHONE,
  FIELD_NAMES.MOBILE_PHONE,
];

// TODO: handle multi-select values for when sexual orientation and pronouns
// are released.
const REQUIRED_WHEN_ALREADY_ANSWERED = [
  PERSONAL_INFO_FIELD_NAMES.GENDER_IDENTITY,
];

/**
 * The mailing address rendered inside a prefill-pattern form app, which is the
 * one combination that gets the functional edit view and the "also update my
 * profile" choice.
 */
export const isPrefillMailingAddress = ({ fieldName, prefillPatternEnabled }) =>
  !!prefillPatternEnabled && fieldName === FIELD_NAMES.MAILING_ADDRESS;

export const usesFormOnlyUpdate = ({ fieldName, prefillPatternEnabled }) =>
  FORM_ONLY_UPDATE_FIELDS.includes(fieldName) ||
  isPrefillMailingAddress({ fieldName, prefillPatternEnabled });

/**
 * Only require a field the Veteran has already answered. Making an unanswered
 * personal information field required would block them from saving anything
 * else on the form.
 */
export const requireFieldBasedOnInitialValue = ({
  formSchema,
  fieldName,
  data,
  editViewData,
}) => {
  const newFormSchema = { ...formSchema };

  if (!REQUIRED_WHEN_ALREADY_ANSWERED.includes(fieldName)) {
    return newFormSchema;
  }

  const initialValues = getInitialFormValues({
    fieldName,
    data,
    modalData: editViewData,
  });

  if (initialValues?.[fieldName]) {
    newFormSchema.required = [fieldName];
  }

  return newFormSchema;
};

/**
 * Everything the edit view needs, assembled from the field's state and the
 * controller's own handlers.
 *
 * `getInitialFormValues` is handed over as a thunk rather than a value because
 * the edit view calls it when it resets the form, not only on mount.
 */
export const getEditViewProps = ({
  allowInternationalPhones,
  apiRoute,
  cancelButtonText,
  convertCleanDataToPayload,
  data,
  editViewData,
  fieldName,
  forceEditView,
  formSchema,
  prefillPatternEnabled,
  saveButtonText,
  shouldFocusCancelButton,
  showMailingAddressUpdateProfileChoice,
  successCallback,
  title,
  uiSchema,
  onCancel,
  onCancelButtonFocused,
}) => {
  const baseProps = {
    getInitialFormValues: () =>
      getInitialFormValues({
        fieldName,
        data,
        modalData: editViewData,
      }),
    onCancel,
    fieldName,
    apiRoute,
    convertCleanDataToPayload,
    uiSchema,
    formSchema: requireFieldBasedOnInitialValue({
      formSchema,
      fieldName,
      data,
      editViewData,
    }),
    title,
    recordCustomProfileEvent,
    forceEditView,
    cancelButtonText,
    saveButtonText,
    showMailingAddressUpdateProfileChoice:
      isPrefillMailingAddress({ fieldName, prefillPatternEnabled }) &&
      showMailingAddressUpdateProfileChoice !== false,
    successCallback,
    // shouldFocusCancelButton and onCancelButtonFocused are used to set focus
    // on the cancel button after the user returns to edit view from the
    // confirm/cancel modal
    shouldFocusCancelButton,
    onCancelButtonFocused,
    allowInternationalPhones,
  };

  if (usesFormOnlyUpdate({ fieldName, prefillPatternEnabled })) {
    return { ...baseProps, useFormOnlyUpdate: true };
  }

  return baseProps;
};
